export function initCustomCarousel() {
    const customCarousel = document.querySelector('.custom-carousel-container') as HTMLElement;
    const track = customCarousel?.querySelector('.custom-carousel-track') as HTMLElement;
    const progress = customCarousel?.querySelector('#carouselProgress') as HTMLElement;
    const carouselItems = customCarousel?.querySelectorAll('.custom-carousel-item');
    const prevButton = customCarousel?.querySelector('#prevCarouselControl');
    const nextButton = customCarousel?.querySelector('#nextCarouselControl');

    let currentTranslate = 0;
    let startX = 0;
    let isDragging = false;
    let previousTranslate = 0;
    let startTime: number;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;
    let animationFrame: number;

    if (!customCarousel || !track)
        return;

    const totalWidth = Array.from(carouselItems).reduce((width, item) =>
        width + (item as HTMLElement).offsetWidth, 0);

    function updateProgressBar() {
        const containerWidth = customCarousel.clientWidth;
        const maxScroll = totalWidth - containerWidth;
        const visiblePortion = (containerWidth / totalWidth) * 100;
        const scrollProgress = (Math.abs(currentTranslate) / maxScroll) * (100 - visiblePortion);
        const finalWidth = Math.min(100, visiblePortion + scrollProgress);
        progress.style.width = `${finalWidth}%`;
    }

    function calculateNextPosition(direction: 'prev' | 'next'): number {
        const containerWidth = customCarousel.clientWidth;
        const currentPosition = Math.abs(currentTranslate);
        const maxScroll = totalWidth - containerWidth;

        if (direction === 'next') {
            const nextPosition = currentPosition + (containerWidth * 0.5);
            return -Math.min(maxScroll, nextPosition);
        } else {
            const nextPosition = currentPosition - (containerWidth * 0.5);
            return -Math.max(0, nextPosition);
        }
    }

    prevButton.addEventListener('click', () => {
        const targetTranslate = calculateNextPosition('prev');
        smoothScroll(targetTranslate);
    });

    nextButton.addEventListener('click', () => {
        const targetTranslate = calculateNextPosition('next');
        smoothScroll(targetTranslate);
    });

    function smoothScroll(targetTranslate: number, duration: number = 300) {
        cancelAnimationFrame(animationFrame);
        velocity = 0;

        const startTranslate = currentTranslate;
        const distance = targetTranslate - startTranslate;
        const startTime = performance.now();

        function animate(currentTime: number) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeProgress = 1 - Math.pow(1 - progress, 3);

            currentTranslate = startTranslate + (distance * easeProgress);
            track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
            updateProgressBar();

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        }

        requestAnimationFrame(animate);
    }

    function boundTranslation(value: number): number {
        const containerWidth = customCarousel.clientWidth;
        const maxScroll = totalWidth - containerWidth;
        return Math.min(0, Math.max(-maxScroll, value));
    }

    function applyMomentum() {
        if (Math.abs(velocity) < 0.1) {
            cancelAnimationFrame(animationFrame);
            return;
        }

        velocity *= 0.95;
        currentTranslate = boundTranslation(currentTranslate + velocity);

        const bounded = boundTranslation(currentTranslate);
        if (bounded !== currentTranslate) {
            velocity = 0;
            currentTranslate = bounded;
            cancelAnimationFrame(animationFrame);
            return;
        }

        track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
        updateProgressBar();

        animationFrame = requestAnimationFrame(applyMomentum);
    }

    function handleDragStart(e: MouseEvent | TouchEvent) {
        if (e instanceof MouseEvent && e.button !== 0) return;

        cancelAnimationFrame(animationFrame);

        isDragging = true;
        startX = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;
        lastX = startX;
        startTime = Date.now();
        lastTime = startTime;
        velocity = 0;
        previousTranslate = currentTranslate;

        track.style.cursor = 'grabbing';
        track.style.transition = 'none';
        document.body.style.userSelect = 'none';
    }

    function handleDrag(e: MouseEvent | TouchEvent) {
        if (!isDragging) return;

        e.preventDefault();
        const currentX = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;
        const currentTime = Date.now();
        const deltaTime = currentTime - lastTime;

        if (deltaTime > 0) {
            velocity = (currentX - lastX) / deltaTime * 16.67;
        }

        lastX = currentX;
        lastTime = currentTime;

        const diff = currentX - startX;
        currentTranslate = boundTranslation(previousTranslate + diff);
        track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
        updateProgressBar();
    }

    function handleDragEnd() {
        if (!isDragging) return;

        isDragging = false;
        track.style.cursor = 'grab';
        document.body.style.userSelect = '';

        if (Math.abs(velocity) > 1) {
            applyMomentum();
        }
    }

    track.addEventListener('mousedown', handleDragStart);
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('mouseleave', handleDragEnd);

    track.addEventListener('touchstart', handleDragStart, {passive: true});
    track.addEventListener('touchmove', handleDrag, {passive: false});
    track.addEventListener('touchend', handleDragEnd);
    track.addEventListener('touchcancel', handleDragEnd);

    let resizeTimeout: number;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
            cancelAnimationFrame(animationFrame);
            currentTranslate = boundTranslation(currentTranslate);
            track.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
            updateProgressBar();
        }, 150);
    });

    updateProgressBar();
}